import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Phone, 
  MessageCircle, 
  Mail, 
  UserCheck,
  Calendar,
  DollarSign,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Clock
} from 'lucide-react';
import { useClientTimeline, type TimelineEvent, type TimelineEventType } from '@/hooks/finance/useClientTimeline';

const EVENT_CONFIG: Record<TimelineEventType, { 
  icon: React.ReactNode; 
  color: string;
  bgColor: string;
}> = {
  contact: { 
    icon: <Phone className="h-4 w-4" />, 
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
  },
  promise: { 
    icon: <Calendar className="h-4 w-4" />, 
    color: 'text-amber-600',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
  },
  promise_fulfilled: { 
    icon: <CheckCircle2 className="h-4 w-4" />, 
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  promise_broken: { 
    icon: <XCircle className="h-4 w-4" />, 
    color: 'text-red-600',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
  },
  payment: { 
    icon: <DollarSign className="h-4 w-4" />, 
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  payment_due: { 
    icon: <AlertTriangle className="h-4 w-4" />, 
    color: 'text-red-600',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
  },
  agreement: { 
    icon: <RefreshCw className="h-4 w-4" />, 
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
  },
};

const CONTACT_ICONS: Record<string, React.ReactNode> = {
  whatsapp: <MessageCircle className="h-4 w-4 text-green-600" />,
  call: <Phone className="h-4 w-4 text-blue-600" />,
  email: <Mail className="h-4 w-4 text-purple-600" />,
  in_person: <UserCheck className="h-4 w-4 text-orange-600" />,
};

interface ClientTimelineModalProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
  clientName: string;
}

export function ClientTimelineModal({
  open,
  onClose,
  contactId,
  clientName,
}: ClientTimelineModalProps) {
  const { data: events, isLoading } = useClientTimeline(contactId);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Timeline - {clientName}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[600px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !events?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum evento encontrado para este cliente.
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-border" />

              <div className="space-y-4">
                {events.map((event, index) => {
                  const config = EVENT_CONFIG[event.type];
                  const eventDate = parseISO(event.date);
                  const contactType = event.metadata?.contact_type as string | undefined;

                  return (
                    <div key={event.id} className="relative flex gap-4">
                      {/* Icon */}
                      <div className={`
                        relative z-10 flex items-center justify-center w-12 h-12 rounded-full
                        ${config.bgColor} ${config.color}
                      `}>
                        {event.type === 'contact' && contactType 
                          ? CONTACT_ICONS[contactType] 
                          : config.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="font-medium">{event.title}</p>
                            {event.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {event.description}
                              </p>
                            )}
                          </div>
                          <time className="text-xs text-muted-foreground whitespace-nowrap">
                            {format(eventDate, "dd/MM/yyyy HH:mm", { locale: ptBR })}
                          </time>
                        </div>

                        {/* Metadata badges */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {event.type === 'contact' && event.metadata?.outcome && (
                            <Badge variant="outline" className="text-xs">
                              {getOutcomeLabel(event.metadata.outcome as string)}
                            </Badge>
                          )}
                          {event.type === 'contact' && event.metadata?.reason && (
                            <Badge variant="secondary" className="text-xs">
                              {getReasonLabel(event.metadata.reason as string)}
                            </Badge>
                          )}
                          {event.type === 'agreement' && event.metadata?.status && (
                            <Badge 
                              variant={event.metadata.status === 'active' || event.metadata.status === 'completed' ? 'default' : 
                                       event.metadata.status === 'rejected' || event.metadata.status === 'defaulted' ? 'destructive' : 'secondary'}
                              className="text-xs"
                            >
                              {getAgreementStatusLabel(event.metadata.status as string)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function getOutcomeLabel(outcome: string): string {
  const labels: Record<string, string> = {
    no_answer: 'Não atendeu',
    promised: 'Prometeu pagar',
    refused: 'Recusou',
    negotiating: 'Negociando',
    partial_payment: 'Pagamento parcial',
    paid: 'Pagou',
    renegotiated: 'Renegociou',
    wrong_number: 'Número errado',
    callback_requested: 'Pediu retorno',
  };
  return labels[outcome] || outcome;
}

function getReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    collection: 'Cobrança',
    follow_up: 'Retorno',
    promise_check: 'Verificar promessa',
    renegotiation: 'Renegociação',
    confirmation: 'Confirmação',
  };
  return labels[reason] || reason;
}

function getAgreementStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending_approval: 'Aguardando Aprovação',
    rejected: 'Rejeitada',
    active: 'Ativo',
    completed: 'Concluído',
    defaulted: 'Inadimplente',
    superseded: 'Substituído',
    cancelled: 'Cancelado',
  };
  return labels[status] || status;
}
